// Temporary stub for task service during Supabase migration
// TODO: Implement with Supabase

import { TaskRow as Task } from '@/models/supabase/Task';
import { supabase, supabaseRpc } from '@/lib/supabase';
import { useAppContextStore } from '@/stores/appContext';
import { invalidateAllDailyCompletionCache } from '@/services/dailyCompletion.service';
import { todayStr } from '@/services/date.service';
import { useAnalytics, type ActivationContext } from '@/composables/useAnalytics';

const analytics = useAnalytics();

const GET_CHILD_TASKS_CACHE_TTL_MS = 4000;
const GET_CHILD_TASKS_CACHE_MAX_ENTRIES = 20;
const GET_USER_TASKS_CACHE_TTL_MS = 4000;

export const taskService = {
  _getChildTasksCache: null as Map<string, { data: Task[]; ts: number }> | null,
  // Short-lived dedup cache for getTasksByUserId. When several per-kid slides are
  // mounted at once (Tasks tab Swiper keeps every slide alive), each fires the
  // same household-scoped tasks query — Sentry flagged this as an N+1 (HARMONIA-9).
  // Keyed by household id (or `user:<id>` for the RPC fallback) so concurrent
  // loads share one request. Invalidated alongside the child-tasks cache.
  _getUserTasksCache: null as Map<string, { data: Task[]; ts: number }> | null,

  _householdTasksShared: new Map<string, { data: Task[]; ts: number }>(),
  _householdTasksSharedInFlight: new Map<string, Promise<Task[]>>(),
  // Lectures de getChildTasks en vol, par enfant.
  _childTasksInFlight: new Map<string, Promise<Task[]>>(),

  _invalidateGetChildTasksCache() {
    this._getChildTasksCache?.clear();
    this._getUserTasksCache?.clear();
    this._householdTasksShared.clear();
  },

  // Créer une nouvelle tâche
  async createTask(data: any, ctx?: ActivationContext): Promise<Task> {
    try {
      // Récupérer le household courant depuis le store
      const appContextStore = useAppContextStore();
      const currentHousehold = appContextStore.currentHousehold;

      if (!currentHousehold) {
        throw new Error('Aucun foyer sélectionné. Veuillez sélectionner un foyer avant de créer une tâche.');
      }

      // Essayer d'abord l'insertion directe avec les types corrects
      const taskData = {
        title: data.title,
        description: data.description || '',
        type: data.type,  // Supabase devrait gérer automatiquement la conversion
        points: data.points || 0,
        frequency: data.frequency || 'daily',
        assignment: data.assignment || 'all',
        child_id: data.child_id || null,
        start_date: data.start_date || todayStr(),
        end_date: data.end_date || null,
        days_of_week: data.days_of_week || [],
        is_active: data.is_active !== undefined ? data.is_active : true,
        validation_count: data.validation_count || 0,
        household_id: currentHousehold.id,  // Ajouter le household_id depuis le store
        icon: data.icon || null,  // Ajouter l'icône
        time_period: data.time_period || null,
        scheduled_time: data.scheduled_time || null,
        allowance_bonus_amount: data.allowance_bonus_amount ?? 0,
        group_id: data.group_id || null,
        children_ids: data.children_ids ?? null,
        weekly_target_count: data.weekly_target_count ?? null,
        rotation_children: data.rotation_children ?? null,
        rotation_anchor_date: data.rotation_anchor_date ?? null,
        rotation_frequency: data.rotation_frequency ?? null,
        // Une semaine sur N, Nième jour du mois (#407) : NOT NULL DEFAULT 1.
        repeat_every_weeks: data.repeat_every_weeks ?? 1,
        repeat_anchor_date: data.repeat_anchor_date ?? null,
        month_week: data.month_week ?? null,
        // ⚠️ PAS de colonne user_id sur tasks : une clé user_id DÉFINIE dans le
        // payload fait rejeter tout l'insert par PostgREST (PGRST204, 400) avant
        // même le SQL. Le formulaire classique ne l'envoyait jamais (undefined,
        // clé absente du JSON), le drawer de groupe si — d'où "Erreur lors de
        // l'enregistrement du groupe". L'auteur ne se persiste que via created_by.
        created_by: data.created_by ?? data.user_id ?? null,
        // Clé posée seulement si la tâche vient d'un modèle : une tâche écrite à
        // la main ne dépend pas de la colonne (PGRST204 si elle manquait).
        ...(data.template_id ? { template_id: data.template_id } : {}),
        // La photo (#354), seulement si le parent en a mis une.
        ...(data.photo ? { photo: data.photo } : {}),
      };

      // Utiliser directement l'API Supabase pour la création
      const { data: task, error } = await supabase
        .from('tasks')
        .insert(taskData)
        .select()
        .single();

      if (error) {
        console.error('Supabase error creating task:', error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        throw error;
      }

      if (!task) {
        console.error('No task returned from insert');
        throw new Error('Failed to create task: no data returned');
      }

      this._invalidateGetChildTasksCache();

      // Deuxième jalon du parcours d'activation. Émis depuis le service et non
      // depuis le store tâches, que les formulaires ne traversent pas.
      analytics.logTaskCreated(
        (task as Task).id,
        taskData.type || 'daily',
        taskData.points,
        undefined,
        { ...ctx, templateId: data.template_id ?? null }
      );

      return task as Task;
    } catch (error) {
      console.error('Error creating task:', error);
      throw error;
    }
  },

  // Minimal columns for hot path (tasks list)
  /**
   * Une lecture de tâches qui échoue ne doit pas se déguiser en « aucune tâche ».
   *
   * Vécu le 2026-08-26 : `member_task_ids` manquait en prod (migration jamais
   * poussée), PostgREST refusait TOUTE lecture passant par
   * `_taskSelectColumns`, et chaque `catch` renvoyait `[]` ou `null`. L'app
   * affichait donc un foyer sans la moindre tâche, et la page Habitudes (qui
   * lit d'autres colonnes) continuait d'en afficher : deux écrans en désaccord,
   * aucun message. L'avertissement ci-dessous existait déjà en commentaire ;
   * ce qui manquait, c'est que la panne se signale à l'exécution.
   */
  _reportQueryFailure(where: string, error: { code?: string; message?: string } | null): void {
    // 42703 colonne inconnue · 42P01 table inconnue · PGRST204 colonne absente
    // du cache de schéma. Les trois disent la même chose : le schéma en base
    // n'est pas celui qu'attend ce build.
    const schemaCodes = ['42703', '42P01', 'PGRST204']
    if (error?.code && schemaCodes.includes(error.code)) {
      console.error(
        `[task.service] ${where} : le schéma en base ne correspond pas à ce build ` +
        `(${error.code} ${error.message}). Une migration n'a pas été appliquée sur cette base. ` +
        `Toutes les lectures de tâches échouent tant qu'elle manque.`,
      )
      return
    }
    console.error(`[task.service] ${where} :`, error)
  },

  // ⚠️ weekly_target_count (20260721150000), rotation_* (20260721170000 +
  // rotation_frequency 20260723120000), group_id (20260722120100),
  // children_ids (20260723100000), member_task_ids (20260824100000) et
  // repeat_* / month_week (20261020160000) exigent
  // le db push en prod AVANT le déploiement du build (sinon PostgREST 400 sur
  // toutes les lectures de tâches).
  _taskSelectColumns: 'id,household_id,created_by,title,description,type,points,frequency,assignment,child_id,start_date,end_date,days_of_week,is_active,validation_count,deleted_at,icon,photo,created_at,time_period,scheduled_time,allowance_bonus_amount,weekly_target_count,rotation_children,rotation_anchor_date,rotation_frequency,repeat_every_weeks,repeat_anchor_date,month_week,group_id,children_ids,member_task_ids',

  // Récupérer les tâches d'un foyer
  async getHouseholdTasks(householdId: string): Promise<Task[]> {
    try {
      const { data, error } = await supabase
        .from('tasks')
        .select(this._taskSelectColumns)
        .eq('household_id', householdId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data ?? []) as unknown as Task[];
    } catch (error) {
      this._reportQueryFailure('getHouseholdTasks', error as any);
      return [];
    }
  },

  async getTasksByHouseholdId(householdId: string): Promise<Task[]> {
    // A fresh authoritative load must not be undone by a stale per-kid cache hit
    // (getTasksByUserId caches by household for ~4s). Drop those caches first.
    this._invalidateGetChildTasksCache();
    const { data, error } = await supabase
      .from('tasks')
      .select(this._taskSelectColumns)
      .eq('household_id', householdId)
      .is('deleted_at', null)
      .order('created_at', { ascending: false });
    if (error) {
      this._reportQueryFailure('getHouseholdTasks', error as any);
      throw error;
    }
    return (data ?? []) as unknown as Task[];
  },

  /**
   * Les tâches du foyer pour un lecteur qui tolère quelques secondes d'âge (le
   * calcul de la série). Cache à part de celui de `getTasksByUserId`, qui
   * retient une liste vide quand sa requête échoue : ici un échec doit lever,
   * sinon la série serait recalculée, puis écrite, sur un foyer sans tâches.
   *
   * `getTasksByHouseholdId` reste la lecture d'autorité : elle repart toujours
   * en base ET vide les caches par enfant. Appelée deux fois par validation
   * pour la série, elle faisait relire les tâches à tout ce qui passait
   * derrière (HARMONIA-77).
   */
  async getHouseholdTasksShared(householdId: string): Promise<Task[]> {
    const cached = this._householdTasksShared.get(householdId);
    if (cached && Date.now() - cached.ts < GET_USER_TASKS_CACHE_TTL_MS) return cached.data;

    const inFlight = this._householdTasksSharedInFlight.get(householdId);
    if (inFlight) return inFlight;

    const request = (async (): Promise<Task[]> => {
      const { data, error } = await supabase
        .from('tasks')
        .select(this._taskSelectColumns)
        .eq('household_id', householdId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
      if (error) {
        this._reportQueryFailure('getHouseholdTasks', error as any);
        throw error;
      }
      return (data ?? []) as unknown as Task[];
    })();

    this._householdTasksSharedInFlight.set(householdId, request);
    try {
      const result = await request;
      this._householdTasksShared.set(householdId, { data: result, ts: Date.now() });
      return result;
    } finally {
      if (this._householdTasksSharedInFlight.get(householdId) === request) {
        this._householdTasksSharedInFlight.delete(householdId);
      }
    }
  },

  // In-flight requests for getTasksByUserId, keyed like _getUserTasksCache.
  // Collapses the burst of identical concurrent calls (N mounted slides) into one.
  _userTasksInFlight: null as Map<string, Promise<Task[]>> | null,

  // Récupérer les tâches d'un utilisateur, scopées au household courant
  async getTasksByUserId(userId: string): Promise<Task[]> {
    const appContextStore = useAppContextStore();
    const householdId = appContextStore.currentHousehold?.id;
    // Same household → same query, so cache/dedup by household (or user for the
    // RPC fallback). This kills the N identical requests fired when every per-kid
    // slide mounts at once (HARMONIA-9).
    const cacheKey = householdId ? `household:${householdId}` : `user:${userId}`;

    const now = Date.now();
    if (!this._getUserTasksCache) this._getUserTasksCache = new Map();
    const cached = this._getUserTasksCache.get(cacheKey);
    if (cached && now - cached.ts < GET_USER_TASKS_CACHE_TTL_MS) {
      return cached.data;
    }

    if (!this._userTasksInFlight) this._userTasksInFlight = new Map();
    const inFlight = this._userTasksInFlight.get(cacheKey);
    if (inFlight) return inFlight;

    const request = (async (): Promise<Task[]> => {
      try {
        if (householdId) {
          // Filtrer par household courant pour ne pas mélanger les tâches entre foyers
          const { data, error } = await supabase
            .from('tasks')
            .select(this._taskSelectColumns)
            .eq('household_id', householdId)
            .is('deleted_at', null)
            .order('created_at', { ascending: false });

          if (error) {
            this._reportQueryFailure('getTasksByUserId', error as any);
            throw error;
          }
          return (data ?? []) as unknown as Task[];
        }

        // Fallback: pas de household courant, utiliser le RPC (tous les foyers)
        const { data, error } = await supabaseRpc.rpc('get_user_tasks', {
          p_user_id: userId
        });

        if (error) {
          this._reportQueryFailure('getTasksByUserId', error as any);
          throw error;
        }

        return data as Task[];
      } catch (error) {
        this._reportQueryFailure('getTasksByUserId', error as any);
        return [];
      }
    })();

    this._userTasksInFlight.set(cacheKey, request);
    try {
      const result = await request;
      this._getUserTasksCache!.set(cacheKey, { data: result, ts: Date.now() });
      return result;
    } finally {
      if (this._userTasksInFlight!.get(cacheKey) === request) {
        this._userTasksInFlight!.delete(cacheKey);
      }
    }
  },

  // Récupérer une tâche par son ID
  /**
   * `null` signifie EXACTEMENT « aucune ligne pour cet identifiant ».
   * Toute autre défaillance (schéma, réseau, RLS en erreur) est levée, pour que
   * l'appelant puisse dire « impossible de charger » plutôt que « n'existe
   * plus », deux phrases qui n'appellent pas la même réaction.
   */
  async getTaskByIdOrFail(taskId: string): Promise<Task | null> {
    // Select direct avec la liste de colonnes complète. L'ancienne RPC
    // get_task_by_id_simple s'arrêtait à `icon` : time_period/scheduled_time
    // manquaient, donc le drawer d'édition perdait l'horaire (et l'aurait
    // effacé au save).
    const { data, error } = await supabase
      .from('tasks')
      .select(this._taskSelectColumns)
      .eq('id', taskId)
      .maybeSingle();

    if (error) {
      this._reportQueryFailure('getTaskById', error);
      throw error;
    }

    return (data as unknown as Task) ?? null;
  },

  /** Contrat historique conservé : `null` en cas d'échec comme d'absence.
      Les appelants qui savent distinguer utilisent `getTaskByIdOrFail`. */
  async getTaskById(taskId: string): Promise<Task | null> {
    try {
      return await this.getTaskByIdOrFail(taskId);
    } catch {
      return null;
    }
  },

  // Récupérer les tâches d'un enfant (court cache pour dédupliquer les appels quand plusieurs onglets sont montés)
  async getChildTasks(childId: string): Promise<Task[]> {
    const now = Date.now();
    if (!this._getChildTasksCache) this._getChildTasksCache = new Map();
    const entry = this._getChildTasksCache.get(childId);
    if (entry && now - entry.ts < GET_CHILD_TASKS_CACHE_TTL_MS) {
      return entry.data;
    }
    // Deux lecteurs simultanés pour le même enfant partagent la même requête :
    // le cache ci-dessus ne sert qu'une fois la première revenue.
    const pending = this._childTasksInFlight.get(childId);
    if (pending) return pending;
    const request = this._fetchChildTasks(childId, now);
    this._childTasksInFlight.set(childId, request);
    try {
      return await request;
    } finally {
      if (this._childTasksInFlight.get(childId) === request) this._childTasksInFlight.delete(childId);
    }
  },

  async _fetchChildTasks(childId: string, now: number): Promise<Task[]> {
    if (!this._getChildTasksCache) this._getChildTasksCache = new Map();
    try {
      // Get current household to scope tasks correctly
      const appContextStore = useAppContextStore();
      const householdId = appContextStore.currentHousehold?.id;

      let query = supabase
        .from('tasks')
        .select(this._taskSelectColumns)
        // children_ids.cs.{x} : routines multi-enfants dont l'enfant fait partie ;
        // rotation_children.cs.{x} : tâches en rotation où l'enfant fait partie du
        // cycle (TW #17468676). Les deux ont assignment=specific + child_id NULL
        // (invisibles aux anciens clients).
        // Les routines (type='group') passent par le MÊME filtre que les autres
        // tâches : une routine ne s'affiche que chez les enfants qu'elle cible.
        // C'est le seul point d'application de cette règle — syncGroupEntries ne
        // connaît pas l'enfant, il dérive ce qu'on lui donne. Depuis que ses
        // membres peuvent être partagés avec un autre enfant (member_task_ids),
        // filtrer sur les membres ferait apparaître la routine du frère.
        .or(`child_id.eq."${childId}",assignment.eq.all,children_ids.cs.{${childId}},rotation_children.cs.{${childId}}`)
        .eq('is_active', true)
        .is('deleted_at', null);

      if (householdId) {
        query = query.eq('household_id', householdId);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        this._reportQueryFailure('getChildTasks', error as any);
        throw error;
      }

      const result = (data ?? []) as unknown as Task[];
      if (this._getChildTasksCache.size >= GET_CHILD_TASKS_CACHE_MAX_ENTRIES) {
        const oldestKey = this._getChildTasksCache.keys().next().value;
        if (oldestKey !== undefined) this._getChildTasksCache.delete(oldestKey);
      }
      this._getChildTasksCache.set(childId, { data: result, ts: now });
      return result;
    } catch (error) {
      this._reportQueryFailure('getChildTasks', error as any);
      return [];
    }
  },

  // Récupérer les tâches d'un enfant pour une date spécifique
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async getChildTasksForDate(childId: string, date: string): Promise<Task[]> {
    try {
      const tasks = await this.getChildTasks(childId);
      // Pour l'instant, on retourne simplement toutes les tâches de l'enfant
      // Une logique plus avancée pourrait être ajoutée pour filtrer par date
      return tasks;
    } catch (error) {
      this._reportQueryFailure('getChildTasksForDate', error as any);
      return [];
    }
  },

  // Mettre à jour une tâche
  async updateTask(taskId: string, data: any): Promise<void> {
    try {
      // Utiliser directement l'API Supabase pour la mise à jour
      const updateData = {
        title: data.title || '',
        description: data.description || '',
        type: data.type || 'recurring',
        // ?? et pas || : une tâche neutre vaut 0 point, `0 || 1` l'écrasait en 1
        // à CHAQUE édition (la création utilise déjà ?? via `|| 0`).
        points: data.points ?? 1,
        frequency: data.frequency || 'daily',
        assignment: data.assignment || 'all',
        child_id: data.child_id || null,
        start_date: data.start_date || todayStr(),
        end_date: data.end_date || null,
        days_of_week: data.days_of_week || [0, 1, 2, 3, 4],
        is_active: data.is_active ?? true,
        validation_count: data.validation_count || 0,
        icon: data.icon || null,
        time_period: data.time_period || null,
        scheduled_time: data.scheduled_time || null,
        allowance_bonus_amount: data.allowance_bonus_amount ?? 0,
        updated_at: new Date().toISOString()
      };

      // La photo (#354) : seulement si l'appelant la fournit (null la retire).
      if (data.photo !== undefined) {
        (updateData as any).photo = data.photo;
      }
      // Ne jamais effacer children_ids par omission (vieux appels sans le champ)
      if (data.children_ids !== undefined) {
        (updateData as any).children_ids = data.children_ids;
      }
      // Même règle pour le lien de groupe : seulement si explicitement fourni.
      if (data.group_id !== undefined) {
        (updateData as any).group_id = data.group_id;
      }
      // Même précaution pour l'objectif hebdo (weekly_target_count)
      if (data.weekly_target_count !== undefined) {
        (updateData as any).weekly_target_count = data.weekly_target_count;
      }
      // Même précaution pour la rotation (les deux champs vont ensemble)
      if (data.rotation_children !== undefined) {
        (updateData as any).rotation_children = data.rotation_children;
      }
      if (data.rotation_anchor_date !== undefined) {
        (updateData as any).rotation_anchor_date = data.rotation_anchor_date;
      }
      if (data.rotation_frequency !== undefined) {
        (updateData as any).rotation_frequency = data.rotation_frequency;
      }
      // Le calendrier espacé (#407) : seulement si l'appelant le connaît.
      for (const key of ['repeat_every_weeks', 'repeat_anchor_date', 'month_week'] as const) {
        if (data[key] !== undefined) (updateData as any)[key] = data[key];
      }

      const { error } = await supabase
        .from('tasks')
        .update(updateData as any)
        .eq('id', taskId);

      if (error) {
        console.error('Error updating task:', error);
        throw error;
      }

      this._invalidateGetChildTasksCache();
      // Invalidate daily completion cache so period/time changes are picked up
      invalidateAllDailyCompletionCache();
    } catch (error) {
      console.error('Error updating task:', error);
      throw error;
    }
  },

  // Mettre à jour le statut de complétion d'une tâche
  // Lier/délier une tâche à un groupe SANS passer par updateTask (qui
  // réécrit tous les champs avec ses défauts). Update ciblé group_id.
  async setTaskGroup(taskId: string, groupId: string | null): Promise<void> {
    const { error } = await supabase
      .from('tasks')
      .update({ group_id: groupId, updated_at: new Date().toISOString() } as any)
      .eq('id', taskId);
    if (error) {
      console.error('Error linking task to group:', error);
      throw error;
    }
    this._invalidateGetChildTasksCache();
    invalidateAllDailyCompletionCache();
  },

  // Membres d'une routine. Le lien vit côté groupe (tasks.member_task_ids) :
  // une tâche partagée par deux enfants peut ainsi appartenir à la routine de
  // chacun, ce que la colonne unique tasks.group_id interdisait.
  //
  // group_id reste écrite comme membership PRIMAIRE : les versions de l'app
  // déjà en production ne connaissent qu'elle et doivent continuer de dériver
  // leurs entrées de groupe. Règle : un membre garde le groupe qui l'a pris en
  // premier ; s'il en sort, il retombe sur une autre de ses routines, ou sur
  // null s'il n'en a plus. Une tâche partagée n'apparaît donc que dans une
  // routine sur les anciennes versions — dégradé, jamais cassé.
  async setGroupMembers(groupId: string, memberIds: string[], householdId: string): Promise<void> {
    const { data: groupsData, error: groupsError } = await supabase
      .from('tasks')
      .select('id, member_task_ids, created_at')
      .eq('household_id', householdId)
      .eq('type', 'group')
      .is('deleted_at', null)
      .order('created_at', { ascending: true });
    if (groupsError) {
      console.error('Error loading groups for membership update:', groupsError);
      throw groupsError;
    }

    const groups = (groupsData ?? []) as unknown as Array<{ id: string; member_task_ids: string[] | null }>;

    // État de départ = tableau du groupe UNION les tâches qui le pointent
    // encore par group_id. Une routine éditée depuis une ancienne version peut
    // avoir un membre que son tableau ignore ; sans cette union, ce membre
    // sortirait de la liste sans que sa membership primaire soit nettoyée.
    const { data: legacyData, error: legacyError } = await supabase
      .from('tasks')
      .select('id')
      .eq('group_id', groupId)
      .is('deleted_at', null);
    if (legacyError) {
      console.error('Error loading legacy group members:', legacyError);
      throw legacyError;
    }
    const previous = [...new Set([
      ...(groups.find(g => g.id === groupId)?.member_task_ids ?? []),
      ...((legacyData ?? []) as Array<{ id: string }>).map(row => row.id),
    ])];
    const next = [...new Set(memberIds)];

    const { error } = await supabase
      .from('tasks')
      .update({ member_task_ids: next, updated_at: new Date().toISOString() } as any)
      .eq('id', groupId);
    if (error) {
      console.error('Error updating group members:', error);
      throw error;
    }

    // Membership primaire recalculée pour les seules tâches dont
    // l'appartenance a bougé, à partir de l'état APRÈS écriture.
    const touched = [...new Set([...previous, ...next])]
      .filter(id => previous.includes(id) !== next.includes(id));
    if (touched.length === 0) {
      this._invalidateGetChildTasksCache();
      invalidateAllDailyCompletionCache();
      return;
    }
    const after = groups.map(g => (g.id === groupId ? { ...g, member_task_ids: next } : g));
    for (const taskId of touched) {
      const primary = after.find(g => g.member_task_ids?.includes(taskId))?.id ?? null;
      await this.setTaskGroup(taskId, primary);
    }

    this._invalidateGetChildTasksCache();
    invalidateAllDailyCompletionCache();
  },

  async updateTaskCompletion(taskId: string, isCompleted: boolean): Promise<void> {
    try {
      await this.updateTask(taskId, { is_completed: isCompleted });
    } catch (error) {
      console.error('Error updating task completion:', error);
      throw error;
    }
  },

  // Supprimer une tâche (soft delete)
  async deleteTask(taskId: string): Promise<void> {
    try {
      // Signal de désengagement : un foyer qui supprime ses tâches décroche.
      // Émis ici, seul point commun aux quatre écrans de suppression et au store.
      analytics.logTaskDeleted(taskId);
      // Utiliser la fonction RPC soft_delete_task pour marquer la tâche comme supprimée
      const { error: rpcError } = await supabaseRpc.rpc('soft_delete_task', {
        p_task_id: taskId
      });

      if (rpcError) {
        // Fallback en cas d'erreur avec la fonction RPC
        if (rpcError.code === '42883') { // Fonction non trouvée
          console.warn('soft_delete_task function not found, falling back to manual soft delete');

          // Fallback manuel pour le soft delete
          const { error } = await supabase
            .from('tasks')
            .update({
              deleted_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            })
            .eq('id', taskId)
            .is('deleted_at', null); // Seulement si pas déjà supprimée

          if (error) throw error;
        } else {
          throw rpcError;
        }
      }
      this._invalidateGetChildTasksCache();
    } catch (error) {
      console.error('Error soft deleting task:', error);
      throw error;
    }
  },

  // Restaurer une tâche supprimée
  async restoreTask(taskId: string): Promise<void> {
    try {
      const { error: rpcError } = await supabaseRpc.rpc('restore_task', {
        p_task_id: taskId
      });

      if (rpcError) {
        // Fallback en cas d'erreur avec la fonction RPC
        if (rpcError.code === '42883') { // Fonction non trouvée
          console.warn('restore_task function not found, falling back to manual restore');

          const { error } = await supabase
            .from('tasks')
            .update({
              deleted_at: null,
              updated_at: new Date().toISOString()
            })
            .eq('id', taskId)
            .not('deleted_at', 'is', null); // Seulement si actuellement supprimée

          if (error) throw error;
        } else {
          throw rpcError;
        }
      }
      this._invalidateGetChildTasksCache();
    } catch (error) {
      console.error('Error restoring task:', error);
      throw error;
    }
  },

  // Obtenir une tâche par ID (y compris les tâches supprimées - pour l'historique)
  async getTaskByIdWithDeleted(taskId: string): Promise<Task | null> {
    try {
      const { data, error } = await supabaseRpc.rpc('get_task_by_id_with_deleted', {
        p_task_id: taskId
      });

      if (error) {
        console.error('Error fetching task with deleted:', error);

        // Fallback direct query
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('tasks')
          .select(this._taskSelectColumns)
          .eq('id', taskId)
          .single();

        if (fallbackError) throw fallbackError;
        return fallbackData as unknown as Task;
      }

      return data && data.length > 0 ? data[0] as Task : null;
    } catch (error) {
      console.error('Error fetching task by ID with deleted:', error);
      return null;
    }
  },

  // Obtenir plusieurs tâches par IDs (y compris les tâches supprimées - pour l'historique)
  async getTasksByIdsWithDeleted(taskIds: string[]): Promise<Task[]> {
    try {
      if (taskIds.length === 0) return [];

      const { data, error } = await supabaseRpc.rpc('get_tasks_by_ids_with_deleted', {
        p_task_ids: taskIds
      });

      if (error) {
        console.error('Error fetching tasks with deleted:', error);

        // Fallback direct query
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('tasks')
          .select(this._taskSelectColumns)
          .in('id', taskIds);

        if (fallbackError) throw fallbackError;
        return fallbackData as unknown as Task[];
      }

      return data as Task[];
    } catch (error) {
      console.error('Error fetching tasks by IDs with deleted:', error);
      return [];
    }
  },

  // Get active tasks for a user
  async getActiveTasks(userId: string): Promise<Task[]> {
    return this.getTasksByUserId(userId);
  },

  // Get completed tasks by child
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async getCompletedTasksByChild(childId: string): Promise<any[]> {
    // TODO: Implement with proper completed tasks table
    return [];
  },

  // Complete a task
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async completeTask(taskId: string, childId: string): Promise<Task> {
    // TODO: Implement task completion logic
    const task = await this.getTaskById(taskId);
    if (!task) throw new Error('Task not found');
    return task;
  },
};
